import { ReactNode, useEffect, useMemo, useState } from "react";

// Gleap CI palette — values copied from gleap-frontend's
// styles/scss-variable.scss. Keep in sync with the matching
// GLEAP_AVATAR_COLORS in gleap-frontend/src/components/UserAvatar/UserAvatar.tsx
// so the same contact gets a visually-identical fallback in the
// dashboard and the messenger.
export const GLEAP_AVATAR_COLORS = ["#0069D3", "#DCE8FD"];

// "Talia Friedman" -> "TF", "oren.shalem@briya.com" -> "O". Restricted to
// alphanumerics so the result can be inlined into SVG markup without
// escaping.
const initialsFromName = (name?: string): string => {
  if (!name) return "";
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^A-Za-zÀ-ÿ0-9]/g, ""))
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

// Local initials avatar as a data URI — mirrors the look of gravatar's
// `?d=initials` default that the remote URL would have shown. Emitted as an
// <img> (not inline <svg>) because several avatar containers style/position
// only their img children (e.g. the stacked team avatars via
// img:nth-of-type(...)); an inline <svg> would escape those rules and shift
// its siblings' nth-of-type indices. The square is rounded by the existing
// img { border-radius } rules, same as the remote images.
const generatedAvatarDataUri = (name?: string): string => {
  const [background, foreground] = GLEAP_AVATAR_COLORS;
  const initials = initialsFromName(name);
  const text = initials
    ? `<text x="32" y="32" fill="${foreground}" font-family="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif" font-size="24" font-weight="600" text-anchor="middle" dominant-baseline="central">${initials}</text>`
    : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="${background}"/>${text}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

interface AvatarImageProps {
  src?: string;
  alt?: string;
  className?: string;
  // Explicit fallback element (e.g. the Gleap bot icon). When provided it is
  // rendered instead of the generated initials avatar on error / when there's
  // no src.
  fallback?: ReactNode;
}

// Renders an avatar image that degrades gracefully to a locally-generated
// initials avatar when the remote image can't load — gravatar or the CDN
// being blocked by an ad/privacy blocker or network policy, a deleted profile
// image, a transient CDN failure, etc. Without this fallback the browser
// shows its broken-image glyph for every row (ticket #135875).
export const AvatarImage = ({
  src,
  alt,
  className,
  fallback,
}: AvatarImageProps) => {
  const [errored, setErrored] = useState(false);

  // Reset on src change so a slot reused for a different (working) image can
  // recover after a previous failure.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const fallbackSrc = useMemo(() => generatedAvatarDataUri(alt), [alt]);

  if (!src || errored) {
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    // A data URI can't fail to load, so no onError needed here.
    return <img src={fallbackSrc} alt={alt} className={className} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErrored(true)}
    />
  );
};

export default AvatarImage;
